import React from "react";
import { observer } from "mobx-react";
import { Box, Paper, Tooltip, useTheme } from "@mui/material";
import dayjs from "dayjs";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { AlertsTrendDay } from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardSummary";
import { AlertsDashboardStore } from "../../../../stores/dashboards/AlertsDashboardStore";
import {
  RedemptionEmptyState,
  RedemptionSectionLabel,
} from "../redemption/RedemptionSection";
import { formatAlertsCount } from "./alertsDashboardFormat";

const SLOT = 10;
const BAR = 7;
const HEIGHT = 100;
const CHART_PX = 200;
const X_LABELS = 6;

interface AlertsTrendChartProps {
  store: AlertsDashboardStore;
}

const dayTooltip = (day: AlertsTrendDay) => (
  <Box sx={{ fontSize: "0.75rem", lineHeight: 1.5 }}>
    <Box sx={{ fontWeight: 700 }}>
      {dayjs(day.day).format("D MMM YYYY")} (UTC)
    </Box>
    <Box>Raised: {formatAlertsCount(day.raised)}</Box>
    <Box>· Admin: {formatAlertsCount(day.admin_raised)}</Box>
    <Box>· Customer: {formatAlertsCount(day.customer_raised)}</Box>
    <Box>Emails sent: {formatAlertsCount(day.emails_sent)}</Box>
    <Box>Emails delivered: {formatAlertsCount(day.emails_delivered)}</Box>
    <Box sx={{ mt: 0.5, opacity: 0.8 }}>Click to list the alerts</Box>
  </Box>
);

/** Stacked bars (admin / customer raised) plus email sent / delivered lines. Day click → `day:YYYY-MM-DD`. */
const AlertsTrendChart: React.FC<AlertsTrendChartProps> = ({ store }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const summary = store.summaryResult;

  if (!summary || store.summaryError) {
    return null;
  }

  const trend = summary.data.trend;
  const days = trend?.days ?? [];
  const audience = summary.meta.audience;
  const showEmails = audience !== "ADMIN";
  const adminColor = colors.darkBlue;
  const customerColor = colors.purple;
  const sentColor = theme.palette.goldenYellow.main;
  const deliveredColor = colors.successGreen;

  const peak = days.reduce(
    (max, day) =>
      Math.max(
        max,
        day.raised,
        showEmails ? day.emails_sent : 0,
        showEmails ? day.emails_delivered : 0,
      ),
    0,
  );
  const scale = peak > 0 ? HEIGHT / peak : 0;
  const width = Math.max(days.length, 1) * SLOT;
  const y = (value: number) => HEIGHT - value * scale;
  const linePoints = (pick: (day: AlertsTrendDay) => number) =>
    days
      .map((day, index) => `${index * SLOT + SLOT / 2},${y(pick(day))}`)
      .join(" ");

  const labelStep = Math.max(1, Math.ceil(days.length / X_LABELS));
  const legend = [
    { label: "Admin raised", color: adminColor, line: false },
    { label: "Customer raised", color: customerColor, line: false },
    ...(showEmails
      ? [
          { label: "Emails sent", color: sentColor, line: true },
          { label: "Emails delivered", color: deliveredColor, line: true },
        ]
      : []),
  ];

  const openDay = (day: AlertsTrendDay) => {
    try {
      void store.openDrilldown(
        day.metricId,
        `Raised on ${day.day}`,
        trend.how,
        "trend_day_click",
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box>
      <RedemptionSectionLabel label="Daily trend" how={trend?.how} />
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, borderColor: colors.lightGray }}
      >
        {days.length === 0 || peak === 0 ? (
          <RedemptionEmptyState message="No alerts raised in this range." />
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
              {legend.map((item) => (
                <Box
                  key={item.label}
                  sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                >
                  <Box
                    sx={{
                      width: item.line ? 14 : 10,
                      height: item.line ? 3 : 10,
                      borderRadius: item.line ? 999 : 0.5,
                      bgcolor: item.color,
                    }}
                  />
                  <CustomTypography
                    variant="body4"
                    color={colors.mutedSlateText}
                  >
                    {item.label}
                  </CustomTypography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: CHART_PX,
                  minWidth: 28,
                  textAlign: "right",
                }}
              >
                <CustomTypography variant="body4" color={colors.greyTextColor}>
                  {formatAlertsCount(peak)}
                </CustomTypography>
                <CustomTypography variant="body4" color={colors.greyTextColor}>
                  0
                </CustomTypography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  component="svg"
                  viewBox={`0 0 ${width} ${HEIGHT}`}
                  preserveAspectRatio="none"
                  sx={{
                    display: "block",
                    width: "100%",
                    height: CHART_PX,
                    borderBottom: `1px solid ${colors.lightGray}`,
                  }}
                >
                  {days.map((day, index) => {
                    const x = index * SLOT + (SLOT - BAR) / 2;
                    const adminH = day.admin_raised * scale;
                    const customerH = day.customer_raised * scale;
                    return (
                      <g key={day.day}>
                        <rect
                          x={x}
                          y={HEIGHT - adminH}
                          width={BAR}
                          height={adminH}
                          fill={adminColor}
                        />
                        <rect
                          x={x}
                          y={HEIGHT - adminH - customerH}
                          width={BAR}
                          height={customerH}
                          fill={customerColor}
                        />
                      </g>
                    );
                  })}
                  {showEmails ? (
                    <>
                      <polyline
                        points={linePoints((day) => day.emails_sent)}
                        fill="none"
                        stroke={sentColor}
                        strokeWidth={2}
                        vectorEffect="non-scaling-stroke"
                      />
                      <polyline
                        points={linePoints((day) => day.emails_delivered)}
                        fill="none"
                        stroke={deliveredColor}
                        strokeWidth={2}
                        vectorEffect="non-scaling-stroke"
                      />
                    </>
                  ) : null}
                  {days.map((day, index) => (
                    <Tooltip
                      key={`hit-${day.day}`}
                      title={dayTooltip(day)}
                      arrow
                      placement="top"
                    >
                      <rect
                        x={index * SLOT}
                        y={0}
                        width={SLOT}
                        height={HEIGHT}
                        fill="transparent"
                        style={{ cursor: "pointer" }}
                        onClick={() => openDay(day)}
                      />
                    </Tooltip>
                  ))}
                </Box>
                <Box sx={{ position: "relative", height: 18, mt: 0.5 }}>
                  {days.map((day, index) =>
                    index % labelStep === 0 ? (
                      <CustomTypography
                        key={`label-${day.day}`}
                        variant="body4"
                        color={colors.greyTextColor}
                        sx={{
                          position: "absolute",
                          left: `${((index + 0.5) / days.length) * 100}%`,
                          transform: "translateX(-50%)",
                          whiteSpace: "nowrap",
                          fontSize: "0.68rem",
                        }}
                      >
                        {dayjs(day.day).format("D MMM")}
                      </CustomTypography>
                    ) : null,
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default observer(AlertsTrendChart);
